/**
 * The one "the AI is working" screen for the whole survey.
 *
 * Extracted from SurveySession, where it was written inline for follow-up generation and the
 * redundancy-skip check. The AI dynamic section needs the identical thing — it generates every
 * turn with a live model call — and a section that invents its own spinner and its own wording
 * reads as a different product mid-interview, which is exactly what happened the first time.
 *
 * Presentational only: the caller owns which message to show and when.
 */
import React from 'react';

interface SurveyAnalyzingIndicatorProps {
  /** The headline message. Callers pass one of UI_TEXT's own analyzing messages. */
  message: string;
  /** Optional quieter second line, e.g. "Preparing your next question…". */
  detail?: string;
  /** The redundancy-skip check uses the brand teal; everything else uses blue. */
  accent?: 'blue' | 'teal';
}

const ACCENT_CLASS: Record<'blue' | 'teal', string> = {
  blue: 'border-blue-500',
  teal: 'border-[#07E5D1]',
};

export const SurveyAnalyzingIndicator: React.FC<SurveyAnalyzingIndicatorProps> = ({
  message, detail, accent = 'blue',
}) => (
  <div className="flex items-center justify-center py-20">
    {/* No role="status" here: the survey already has ONE live region (the timer nudge), and a
        second one broke it. Copied from SurveySession verbatim — improving the markup while
        extracting it is how an extraction stops being an extraction. */}
    <div className="text-center">
      <div
        className={`w-24 h-24 border-4 ${ACCENT_CLASS[accent]} border-t-transparent rounded-full animate-spin mx-auto mb-6`}
      />
      <p className="text-lg font-medium text-gray-900">{message}</p>
      {detail && <p className="mt-2 text-sm text-gray-500">{detail}</p>}
    </div>
  </div>
);

export default SurveyAnalyzingIndicator;
